import {useEffect, useMemo, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useApiRequest} from "../../../common/hooks/useApiRequest";
import {StagedWord, WordWithId} from "../../../common/types/words";
import {useDeck} from "../../deck-management/context/DeckContext";
import {TopicVocabulary, Unit, UnitVocabulary} from "../types/vocabulary_types";
import UseTwoStepUpdate from "./useTwoStepUpdate";

export interface VocabularyActionDispatch<T extends WordWithId = WordWithId> {
    add?: (onAddedPromise: Promise<T>) => void
    update?: (updatedWord: T, updatedWordPromise: Promise<T>) => void
    delete?: (onDeletedPromise: Promise<string>) => void
}

function sortUnits(units: Unit[]): Unit[] {
    return [...units].sort((left, right) => {
        if (left.isCustom !== right.isCustom) {
            return Number(left.isCustom) - Number(right.isCustom);
        }
        return left.order - right.order;
    });
}

export function useVocabulary() {
    const [vocabulary, setVocabulary] = useState<Map<number, TopicVocabulary[]>>(new Map());
    const [loadedUnitIds, setLoadedUnitIds] = useState<Set<number>>(new Set());
    const [tabIndex, setTabIndex] = useState(0);

    const {currentDeck, ready: deckReady, error: deckLoadError} = useDeck();
    const {getRequest} = useApiRequest();

    const {data: unitsData, isPending: unitsPending, isError: unitsLoadError} = useQuery({
        queryKey: ["vocabulary-units", currentDeck?.deckId],
        queryFn: () => getRequest<Unit[]>(`decks/${currentDeck!.deckId}/units`),
        enabled: deckReady,
        staleTime: Infinity,
    });

    const sortedUnits = useMemo(() => sortUnits(unitsData ?? []), [unitsData]);
    const currentUnit = sortedUnits[tabIndex] ?? null;

    useEffect(() => {
        if (!unitsData || !sortedUnits) {
            return;
        }

        const nextVocabulary = new Map<number, TopicVocabulary[]>();
        sortedUnits.forEach((unit) => nextVocabulary.set(unit.id, []));
        setVocabulary(nextVocabulary);
        setLoadedUnitIds(new Set());
        setTabIndex(0);
    }, [unitsData, sortedUnits]);

    const {data: currentUnitVocabulary, isPending: currentUnitPending, isError: currentUnitLoadError} = useQuery({
        queryKey: ["vocabulary-unit", currentDeck?.deckId, currentUnit?.id],
        queryFn: () => getRequest<UnitVocabulary>(`decks/${currentDeck!.deckId}/units/${currentUnit!.id}/words?stages=true`),
        enabled: deckReady && !!currentUnit && !loadedUnitIds.has(currentUnit.id),
        staleTime: Infinity,
    });

    useEffect(() => {
        if (!currentUnitVocabulary) {
            return;
        }

        setVocabulary((previousVocabulary) => {
            const nextVocabulary = new Map(previousVocabulary);
            nextVocabulary.set(currentUnitVocabulary.unitId, currentUnitVocabulary.topics);
            return nextVocabulary;
        });
        setLoadedUnitIds((previousLoadedUnitIds) => new Set(previousLoadedUnitIds).add(currentUnitVocabulary.unitId));
    }, [currentUnitVocabulary]);

    const theSelectedWord = useRef<StagedWord | null>(null);

    const allWords = useMemo(
        () => Array.from(vocabulary.values()).flatMap((topics) => topics.flatMap((topic) => topic.words)),
        [vocabulary]
    );

    function onWordAdded(onAddedPromise: Promise<WordWithId>) {
        onAddedPromise.then((word) => {
            setVocabulary((previousVocabulary) => {
                const nextVocabulary = new Map(previousVocabulary);

                const targetUnitId = sortedUnits.find((unit) => unit.isCustom)?.id;
                if (targetUnitId === undefined) {
                    return previousVocabulary;
                }

                const topics = nextVocabulary.get(targetUnitId);
                if (!topics || topics.length === 0) {
                    return previousVocabulary;
                }

                topics[0].words.push({...word, stage: 0});
                nextVocabulary.set(targetUnitId, topics);
                return nextVocabulary;
            });
        }).catch(() => {});
    }

    const {handleTwoStepUpdate} = UseTwoStepUpdate(
        () => theSelectedWord.current,
        (updatedWord) => {
            setVocabulary((previousVocabulary) => {
                const nextVocabulary = new Map(previousVocabulary);
                for (const topics of nextVocabulary.values()) {
                    for (const topic of topics) {
                        const index = topic.words.findIndex((word) => word.wordId === updatedWord!.wordId);
                        if (index !== -1) {
                            topic.words[index] = updatedWord!;
                            return nextVocabulary;
                        }
                    }
                }
                return previousVocabulary;
            });
        }
    );

    const onWordUpdated = (updatedWord: WordWithId, updateWordPromise: Promise<WordWithId>) => {
        const currentStage = theSelectedWord.current?.stage ?? 0;
        theSelectedWord.current = allWords.find((word) => word.wordId === updatedWord.wordId) || null;
        handleTwoStepUpdate({...updatedWord, stage: currentStage}, updateWordPromise.then((word) => ({...word, stage: currentStage})));
    };

    function onWordDeleted(deletePromise: Promise<string>) {
        deletePromise.then((wordId) => {
            setVocabulary((previousVocabulary) => {
                const nextVocabulary = new Map(previousVocabulary);
                for (const topics of nextVocabulary.values()) {
                    for (const topic of topics) {
                        const index = topic.words.findIndex((word) => word.wordId === wordId);
                        if (index !== -1) {
                            topic.words.splice(index, 1);
                            return nextVocabulary;
                        }
                    }
                }
                return previousVocabulary;
            });
        }).catch(() => {});
    }

    const actionDispatch: VocabularyActionDispatch = {
        add: onWordAdded,
        update: onWordUpdated,
        delete: onWordDeleted,
    };

    return {
        vocabulary,
        sortedUnits,
        currentUnit,
        tabIndex,
        setTabIndex,
        allWords,
        actionDispatch,
        error: Boolean(deckLoadError || unitsLoadError || currentUnitLoadError),
        isReady: deckReady && !unitsPending,
        currentUnitPending,
        hasUnits: sortedUnits.length > 0,
    };
}

export default useVocabulary;
